import { useEffect, useMemo, useState } from "react";
import {
  ArrowSquareOut,
  CaretRight,
  CheckCircle,
  Circle,
  FilePdf,
  PlayCircle,
  Question,
} from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { LoadingState } from "@/components/DashboardParts";
import RoleLayout from "@/components/RoleLayout";
import { api, formatApiError } from "@/lib/api";
import { youtubeEmbedUrl } from "@/lib/youtube";

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [answers, setAnswers] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [activePdfUrl, setActivePdfUrl] = useState("");
  const [isOutlineOpen, setIsOutlineOpen] = useState(false);

  const refresh = () => api.get(`/lms/courses/${courseId}`).then((response) => {
    setCourse(response.data);
    if (!selectedLessonId) {
      setSelectedLessonId(response.data.modules[0]?.lessons[0]?.id || "");
    }
  });

  useEffect(() => { refresh(); }, [courseId]);

  const lessons = useMemo(() => course?.modules.flatMap((module) => module.lessons) || [], [course]);
  const selectedLesson = lessons.find((lesson) => lesson.id === selectedLessonId);
  const completedIds = course?.enrollment?.completed_lesson_ids || [];
  const questions = course?.modules.flatMap((module) => module.quiz) || [];
  const safeResourceUrl = (url) => (url?.startsWith("https://") ? url : "#");
  const defaultVideo = course?.learning_resources?.find((resource) => resource.type === "Video")?.url;
  const embeddedVideoUrl = youtubeEmbedUrl(activeVideoUrl || selectedLesson?.video_url || defaultVideo);
  const activePdf = course?.learning_resources?.find((resource) => resource.url === activePdfUrl);

  const completeLesson = async () => {
    setIsSaving(true);
    try {
      await api.post(`/lms/courses/${courseId}/lessons/${selectedLessonId}/complete`);
      await refresh();
      toast.success("Lesson marked complete");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const submitQuiz = async () => {
    try {
      const response = await api.post(`/lms/courses/${courseId}/quiz`, { answers });
      toast.success(response.data.passed ? `Passed with ${response.data.score}%` : `Score: ${response.data.score}%`);
      await refresh();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (!course) {
    return <LoadingState label="Opening your course" />;
  }

  return (
    <RoleLayout eyebrow={course.category} title={course.title}>
      <div className="player-progress" data-testid="course-player-progress">
        <span>Course completion</span><strong>{course.progress}%</strong>
        <div className="progress-track"><span style={{ width: `${course.progress}%` }} /></div>
      </div>
      <button
        className="mobile-outline-toggle"
        onClick={() => setIsOutlineOpen(!isOutlineOpen)}
        aria-expanded={isOutlineOpen}
        data-testid="toggle-course-outline-button"
      >
        {isOutlineOpen ? "Hide course outline" : "Show course outline"}
      </button>
      <section className="course-player-layout" data-testid="course-player-layout">
        <article className="lesson-stage" data-testid="lesson-stage">
          <div className={`lesson-media ${activePdf ? "pdf-reading-stage" : ""}`} data-testid="lesson-content-stage">
            {activePdf ? (
              <div className="pdf-reader" data-testid="embedded-pdf-reader">
                <div className="pdf-reader-heading"><span>Reading space</span><a href={safeResourceUrl(activePdf.url)} target="_blank" rel="noreferrer" data-testid="open-pdf-new-tab-link">Open in new tab <ArrowSquareOut aria-hidden="true" size={16} /></a></div>
                <iframe src={safeResourceUrl(activePdf.url)} title={`PDF reading: ${activePdf.title}`} loading="lazy" data-testid="embedded-pdf-frame" />
              </div>
            ) : embeddedVideoUrl ? (
              <iframe
                key={embeddedVideoUrl}
                src={embeddedVideoUrl}
                title={`YouTube video: ${selectedLesson?.title || course.title}`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="embedded-youtube-player"
              />
            ) : (
              <PlayCircle aria-hidden="true" size={64} weight="fill" />
            )}
          </div>
          <p className="eyebrow">Current lesson · {selectedLesson?.minutes || 0} min</p>
          <h2 data-testid="selected-lesson-title">{selectedLesson?.title || "Course introduction"}</h2>
          <p className="lesson-copy">Capture the ideas that matter, then translate them into one practical decision in your work.</p>
          <button className="primary-button" disabled={isSaving} onClick={completeLesson} data-testid="complete-lesson-button">
            <CheckCircle aria-hidden="true" size={20} weight="fill" />
            {isSaving ? "Saving" : "Mark lesson complete"}
          </button>
          {course.learning_resources?.length > 0 && (
            <section className="learning-resources" data-testid="course-learning-resources">
              <p className="eyebrow">Course library</p>
              <h3>Videos and reading material</h3>
              <div className="resource-list">
                {course.learning_resources.map((resource) => {
                  const isVideo = resource.type === "Video";
                  const content = <>
                    {isVideo ? <PlayCircle aria-hidden="true" size={25} weight="fill" /> : <FilePdf aria-hidden="true" size={25} weight="duotone" />}
                    <span>
                      <small>{resource.type}</small>
                      <strong>{resource.title}</strong>
                      <em>{resource.description}</em>
                    </span>
                    {isVideo ? <CaretRight aria-hidden="true" size={21} weight="bold" /> : <ArrowSquareOut aria-hidden="true" size={19} />}
                  </>;
                  const selectResource = () => {
                    setActiveVideoUrl(isVideo ? resource.url : "");
                    setActivePdfUrl(isVideo ? "" : resource.url);
                  };
                  return <button className="resource-link" key={resource.id} onClick={selectResource} data-testid={`course-resource-${resource.id}`}>{content}</button>;
                })}
              </div>
            </section>
          )}
          {questions.length > 0 && (
            <section className="quiz-panel" data-testid="course-quiz-panel">
              <p className="eyebrow">Knowledge check</p><h3>Confirm your understanding</h3>
              {questions.map((question) => (
                <fieldset key={question.id} data-testid={`quiz-question-${question.id}`}>
                  <legend>{question.question}</legend>
                  {question.options.map((option, index) => (
                    <label key={option} className="quiz-option">
                      <input type="radio" name={question.id} value={index} onChange={() => setAnswers({ ...answers, [question.id]: index })} data-testid={`quiz-${question.id}-option-${index}`} />
                      {option}
                    </label>
                  ))}
                </fieldset>
              ))}
              <button className="outline-button" onClick={submitQuiz} data-testid="submit-quiz-button"><Question aria-hidden="true" size={20} />Submit assessment</button>
            </section>
          )}
        </article>
        <aside className={`course-outline ${isOutlineOpen ? "outline-open" : ""}`} data-testid="course-outline">
          <div className="outline-heading"><span>Course outline</span><button onClick={() => navigate("/learner/dashboard")} data-testid="back-to-dashboard-button">Close</button></div>
          {course.modules.map((module) => (
            <div className="module-group" key={module.id} data-testid={`module-${module.id}`}>
              <h3>{module.title}</h3>
              {module.lessons.map((lesson) => {
                const completed = completedIds.includes(lesson.id);
                return <button className={`lesson-nav ${selectedLessonId === lesson.id ? "selected" : ""}`} key={lesson.id} onClick={() => { setSelectedLessonId(lesson.id); setActiveVideoUrl(""); setActivePdfUrl(""); setIsOutlineOpen(false); }} data-testid={`lesson-${lesson.id}-button`}>
                  {completed ? <CheckCircle aria-hidden="true" size={18} weight="fill" /> : <Circle aria-hidden="true" size={18} />}
                  <span>{lesson.title}</span><small>{lesson.minutes}m</small>
                </button>;
              })}
            </div>
          ))}
        </aside>
      </section>
    </RoleLayout>
  );
}